/**
 * Transform functions for Accident benefits
 *
 * These functions handle the bi-directional transformation between:
 * - Frontend grid format (flat fields with separate type and amount)
 * - Backend API format (nested objects with type and amount)
 */

import {
  transformTypeAmountToApi,
  transformTypeAmountFromApi,
  transformAgeReductionScheduleToApi,
  transformAgeReductionScheduleFromApi,
  shouldIncludeBenefitInApi,
} from "../shared/builders";

import type { AccidentBenefitsApi, AccidentBenefitsGrid } from "./types";

/**
 * Transforms Accident benefits from frontend grid format to API format
 *
 * Converts flat grid fields into nested API structures for all accident benefits.
 * All benefit fields follow the type/amount pattern and use the shared transform builder.
 *
 * @param benefits - The benefits object from the frontend grid (flat fields)
 * @returns Transformed benefits object matching the API structure (nested objects)
 */
export const transformAccidentBenefitsToApi = (
  benefits: AccidentBenefitsGrid,
): AccidentBenefitsApi => {
  const transformed: AccidentBenefitsApi = {
    objectType: "accident",
  };

  // Coverage Type (simple enum, not nested)
  // Always include - defaults to undefined if not set
  transformed.coverageType = benefits.coverageType;

  // Hospital Admission
  // Always transform - transformTypeAmountToApi handles undefined/notIncluded
  transformed.hospitalAdmission = transformTypeAmountToApi(
    benefits.hospitalAdmissionType,
    benefits.hospitalAdmissionAmount,
  ) as AccidentBenefitsApi["hospitalAdmission"];

  // ICU Admission
  transformed.icuAdmission = transformTypeAmountToApi(
    benefits.icuAdmissionType,
    benefits.icuAdmissionAmount,
  ) as AccidentBenefitsApi["icuAdmission"];

  // Daily Hospital Confinement
  transformed.dailyHospitalConfinement = transformTypeAmountToApi(
    benefits.dailyHospitalConfinementType,
    benefits.dailyHospitalConfinementAmount,
  ) as AccidentBenefitsApi["dailyHospitalConfinement"];

  // Daily ICU Confinement
  transformed.dailyIcuConfinement = transformTypeAmountToApi(
    benefits.dailyIcuConfinementType,
    benefits.dailyIcuConfinementAmount,
  ) as AccidentBenefitsApi["dailyIcuConfinement"];

  // Ambulance - Air
  transformed.ambulanceAir = transformTypeAmountToApi(
    benefits.ambulanceAirType,
    benefits.ambulanceAirAmount,
  ) as AccidentBenefitsApi["ambulanceAir"];

  // Ambulance - Ground
  transformed.ambulanceGround = transformTypeAmountToApi(
    benefits.ambulanceGroundType,
    benefits.ambulanceGroundAmount,
  ) as AccidentBenefitsApi["ambulanceGround"];

  // Emergency Treatment
  transformed.emergencyTreatment = transformTypeAmountToApi(
    benefits.emergencyTreatmentType,
    benefits.emergencyTreatmentAmount,
  ) as AccidentBenefitsApi["emergencyTreatment"];

  // Urgent Care
  transformed.urgentCare = transformTypeAmountToApi(
    benefits.urgentCareType,
    benefits.urgentCareAmount,
  ) as AccidentBenefitsApi["urgentCare"];

  // Initial Physician Office Visit
  transformed.initialPhysicianOfficeVisit = transformTypeAmountToApi(
    benefits.initialPhysicianOfficeVisitType,
    benefits.initialPhysicianOfficeVisitAmount,
  ) as AccidentBenefitsApi["initialPhysicianOfficeVisit"];

  // Dislocations Maximum
  transformed.dislocationsMaximum = transformTypeAmountToApi(
    benefits.dislocationsMaximumType,
    benefits.dislocationsMaximumAmount,
    ["upToDollarAmount"],
  ) as AccidentBenefitsApi["dislocationsMaximum"];

  // Fractures Maximum
  transformed.fracturesMaximum = transformTypeAmountToApi(
    benefits.fracturesMaximumType,
    benefits.fracturesMaximumAmount,
    ["upToDollarAmount"],
  ) as AccidentBenefitsApi["fracturesMaximum"];

  // Lacerations Maximum
  transformed.lacerationsMaximum = transformTypeAmountToApi(
    benefits.lacerationsMaximumType,
    benefits.lacerationsMaximumAmount,
    ["upToDollarAmount"],
  ) as AccidentBenefitsApi["lacerationsMaximum"];

  // Coma
  transformed.coma = transformTypeAmountToApi(
    benefits.comaType,
    benefits.comaAmount,
    ["upToDollarAmount"],
  ) as AccidentBenefitsApi["coma"];

  // Major Diagnostic Exam - X-Ray
  transformed.majorDiagnosticExamXray = transformTypeAmountToApi(
    benefits.majorDiagnosticExamXrayType,
    benefits.majorDiagnosticExamXrayAmount,
    ["upToDollarAmount"],
  ) as AccidentBenefitsApi["majorDiagnosticExamXray"];

  // Major Diagnostic Exam - MRI
  transformed.majorDiagnosticExamMri = transformTypeAmountToApi(
    benefits.majorDiagnosticExamMriType,
    benefits.majorDiagnosticExamMriAmount,
    ["upToDollarAmount"],
  ) as AccidentBenefitsApi["majorDiagnosticExamMri"];

  // Wellness Benefit
  transformed.wellnessBenefit = transformTypeAmountToApi(
    benefits.wellnessBenefitType,
    benefits.wellnessBenefitAmount,
  ) as AccidentBenefitsApi["wellnessBenefit"];

  // Child Organized Sports Enhancement (simple enum)
  // Always include - defaults to undefined if not set
  transformed.childOrganizedSportsEnhancement =
    benefits.childOrganizedSportsEnhancement;

  // Portability (simple enum)
  transformed.portability = benefits.portability;

  // Age Reduction Schedule
  if (benefits.ageReductionScheduleType !== undefined) {
    transformed.ageReductionSchedule = transformAgeReductionScheduleToApi(
      benefits.ageReductionScheduleType,
      benefits.ageReductionScheduleSchedules,
    ) as AccidentBenefitsApi["ageReductionSchedule"];
  }

  // Annual Open Enrollment
  if (shouldIncludeBenefitInApi(benefits.annualOpenEnrollment)) {
    transformed.annualOpenEnrollment = benefits.annualOpenEnrollment;
  }

  // Benefit Class Availability (flat field)
  transformed.benefitClassAvailability = benefits.benefitClassAvailability;

  // Extended (non-v5) fields — CD-390. Sent as a full object (a full-replace
  // PATCH, like the rest of `benefits`) whenever at least one extended field
  // is set, so an edit to any other field on the plan doesn't drop the
  // extended values already on it. Omitted entirely when every extended
  // field is unset, so an edit that never touches these fields doesn't
  // create an empty extended row on the backend.
  const extended = {
    accidentalDeathDismemberment: benefits.accidentalDeathDismemberment,
    eligibilityHours: benefits.eligibilityHours,
  };
  if (
    Object.values(extended).some((value) => value !== undefined && value !== "")
  ) {
    transformed.extended = extended;
  }

  return transformed;
};

/**
 * Transforms Accident benefits from API format to frontend grid format
 *
 * Converts nested API structures into flat grid fields for all accident benefits.
 * All benefit fields follow the type/amount pattern and use the shared transform builder.
 *
 * @param benefits - The benefits object from the API (nested objects)
 * @returns Transformed benefits object matching the grid structure (flat fields)
 */
export const transformAccidentBenefitsFromApi = (
  benefits: AccidentBenefitsApi,
): AccidentBenefitsGrid => {
  const transformed: AccidentBenefitsGrid = {
    objectType: "accident",
  };

  // Coverage Type (simple enum)
  transformed.coverageType = benefits.coverageType;

  // Hospital Admission
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.hospitalAdmission, "hospitalAdmission"),
  );

  // ICU Admission
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.icuAdmission, "icuAdmission"),
  );

  // Daily Hospital Confinement
  Object.assign(
    transformed,
    transformTypeAmountFromApi(
      benefits.dailyHospitalConfinement,
      "dailyHospitalConfinement",
    ),
  );

  // Daily ICU Confinement
  Object.assign(
    transformed,
    transformTypeAmountFromApi(
      benefits.dailyIcuConfinement,
      "dailyIcuConfinement",
    ),
  );

  // Ambulance - Air
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.ambulanceAir, "ambulanceAir"),
  );

  // Ambulance - Ground
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.ambulanceGround, "ambulanceGround"),
  );

  // Emergency Treatment
  Object.assign(
    transformed,
    transformTypeAmountFromApi(
      benefits.emergencyTreatment,
      "emergencyTreatment",
    ),
  );

  // Urgent Care
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.urgentCare, "urgentCare"),
  );

  // Initial Physician Office Visit
  Object.assign(
    transformed,
    transformTypeAmountFromApi(
      benefits.initialPhysicianOfficeVisit,
      "initialPhysicianOfficeVisit",
    ),
  );

  // Dislocations Maximum
  Object.assign(
    transformed,
    transformTypeAmountFromApi(
      benefits.dislocationsMaximum,
      "dislocationsMaximum",
    ),
  );

  // Fractures Maximum
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.fracturesMaximum, "fracturesMaximum"),
  );

  // Lacerations Maximum
  Object.assign(
    transformed,
    transformTypeAmountFromApi(
      benefits.lacerationsMaximum,
      "lacerationsMaximum",
    ),
  );

  // Coma
  Object.assign(transformed, transformTypeAmountFromApi(benefits.coma, "coma"));

  // Major Diagnostic Exam - X-Ray
  Object.assign(
    transformed,
    transformTypeAmountFromApi(
      benefits.majorDiagnosticExamXray,
      "majorDiagnosticExamXray",
    ),
  );

  // Major Diagnostic Exam - MRI
  Object.assign(
    transformed,
    transformTypeAmountFromApi(
      benefits.majorDiagnosticExamMri,
      "majorDiagnosticExamMri",
    ),
  );

  // Wellness Benefit
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.wellnessBenefit, "wellnessBenefit"),
  );

  // Child Organized Sports Enhancement (simple enum)
  transformed.childOrganizedSportsEnhancement =
    benefits.childOrganizedSportsEnhancement;

  // Portability (simple enum)
  transformed.portability = benefits.portability;

  // Age Reduction Schedule
  Object.assign(
    transformed,
    transformAgeReductionScheduleFromApi(benefits.ageReductionSchedule),
  );

  // Annual Open Enrollment (simple enum)
  transformed.annualOpenEnrollment = benefits.annualOpenEnrollment;

  // Benefit Class Availability (flat field)
  transformed.benefitClassAvailability = benefits.benefitClassAvailability;

  // Extended (non-v5) fields — CD-390
  transformed.accidentalDeathDismemberment =
    benefits.extended?.accidentalDeathDismemberment ?? undefined;
  transformed.eligibilityHours =
    benefits.extended?.eligibilityHours ?? undefined;

  return transformed;
};

/** Grid keys that persist under `benefits.extended` — see STD_EXTENDED_GRID_FIELD_KEYS. */
export const ACCIDENT_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> = new Set([
  "accidentalDeathDismemberment",
  "eligibilityHours",
]);
